import { useCallback, useEffect, useState } from "react";
import { getPageFromPath, pageToPath } from "../navigation";
import { readQueryFromSearchString } from "../searchNavigation";
import type {
  NavigateOptions,
  PageHistoryState,
  SearchFocusTarget,
} from "../types";

export function useAppRouting() {
  const [currentPage, setCurrentPage] = useState("home");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocusTarget, setSearchFocusTarget] =
    useState<SearchFocusTarget | null>(null);

  const navigate = useCallback(
    (page: string, options: NavigateOptions = {}) => {
      setCurrentPage(page);

      if (page === "search" && typeof options.state?.searchQuery === "string") {
        setSearchQuery(options.state.searchQuery);
      }

      if (typeof window === "undefined") {
        return;
      }

      const path = pageToPath[page] ?? `/${page}`;
      const searchFromOption = (options.search ?? "").trim();
      const normalizedSearch =
        searchFromOption.length === 0
          ? ""
          : searchFromOption.startsWith("?")
          ? searchFromOption.substring(1)
          : searchFromOption;
      const focusTarget = options.state?.focusTarget;
      const fallbackSearchQuery =
        page === "search" && normalizedSearch
          ? readQueryFromSearchString(normalizedSearch)
          : "";
      const historyState: PageHistoryState = {
        page,
        ...(options.state?.usr ? { usr: options.state.usr } : {}),
        ...(page === "search"
          ? {
              searchQuery:
                typeof options.state?.searchQuery === "string"
                  ? options.state.searchQuery
                  : fallbackSearchQuery
                  ? fallbackSearchQuery
                  : searchQuery,
            }
          : {}),
        ...(focusTarget !== undefined ? { focusTarget } : {}),
      };
      const fullPath = normalizedSearch ? `${path}?${normalizedSearch}` : path;

      if (options.replace) {
        window.history.replaceState(historyState, "", fullPath);
      } else {
        window.history.pushState(historyState, "", fullPath);
      }

      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    },
    [searchQuery]
  );

  const handleNavigateToSearchPage = useCallback(
    (target: SearchFocusTarget) => {
      setSearchFocusTarget(target);
      navigate(target === "categories" ? "categories" : "search", {
        state: { focusTarget: target },
      });
    },
    [navigate]
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const initialPage = getPageFromPath(window.location.pathname);
    setCurrentPage(initialPage);

    const initialState = (window.history.state as PageHistoryState | null) ?? {
      page: initialPage,
    };

    if (initialPage === "search") {
      const initialQuery =
        initialState.searchQuery ??
        readQueryFromSearchString(window.location.search);
      setSearchQuery(initialQuery);
      initialState.searchQuery = initialQuery;
    }

    window.history.replaceState(
      initialState,
      "",
      (pageToPath[initialPage] ?? window.location.pathname) + window.location.search
    );

    const handlePopState = (event: PopStateEvent) => {
      const state = event.state as PageHistoryState | null;
      const fallbackPage = getPageFromPath(window.location.pathname);
      const page = state?.page ?? fallbackPage;
      setCurrentPage(page);

      if (page === "search") {
        setSearchQuery(
          state?.searchQuery ??
            readQueryFromSearchString(window.location.search)
        );
      }

      const focusTargetFromState = state?.focusTarget;
      const nextFocusTarget =
        focusTargetFromState !== undefined
          ? focusTargetFromState
          : page === "categories"
          ? "categories"
          : null;
      setSearchFocusTarget(nextFocusTarget);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return {
    currentPage,
    searchQuery,
    searchFocusTarget,
    navigate,
    handleNavigateToSearchPage,
    clearSearchFocusTarget: () => setSearchFocusTarget(null),
  };
}
